/** Meet bookkeeping: programme, lives, standings and league points. */
import {
  DAY1,
  DAY2,
  EVENTS,
  eventById,
  meetsStandard,
  type EventDef,
  type EventId,
} from './events.js';
import type { MeetResultRow, Programme } from './protocol.js';
import { BEST_EVENT_BONUS, pointsFor, pointsForPlace } from './scoring.js';

export const LIVES = 3;

export function programmeEvents(p: Programme): EventDef[] {
  if (p === 'full') return EVENTS;
  if (p === 'day1') return DAY1;
  if (p === 'day2') return DAY2;
  return [eventById(p)];
}

export function programmeName(p: Programme): string {
  if (p === 'full') return 'Full decathlon';
  if (p === 'day1') return 'Day 1';
  if (p === 'day2') return 'Day 2';
  return eventById(p).name;
}

export interface MeetAthlete {
  id: string;
  marks: Partial<Record<EventId, number | null>>;
  points: Partial<Record<EventId, number>>;
  total: number;
  lives: number;
  eliminated: boolean;
}

export function createMeetAthlete(id: string): MeetAthlete {
  return { id, marks: {}, points: {}, total: 0, lives: LIVES, eliminated: false };
}

/** Record an event mark: points, and a life lost if the standard was missed. */
export function applyMark(
  a: MeetAthlete,
  ev: EventDef,
  mark: number | null,
  season: number,
): { points: number; passed: boolean } {
  const points = pointsFor(ev, mark);
  const passed = meetsStandard(ev, mark, season);
  a.marks[ev.id] = mark;
  a.points[ev.id] = points;
  a.total += points;
  if (!passed) {
    a.lives = Math.max(0, a.lives - 1);
    if (a.lives === 0) a.eliminated = true;
  }
  return { points, passed };
}

/** Sort by total. Equal totals share their place; eliminated athletes retain their total. */
export function standings<T extends { total: number }>(athletes: T[]): T[] {
  return [...athletes].sort((a, b) => b.total - a.total);
}

/** Places for one event: better mark first; null marks last, in input order. */
export function eventPlaces(
  ev: EventDef,
  marks: { id: string; mark: number | null }[],
): Map<string, number> {
  const lower = ev.unit === 'seconds';
  const sorted = [...marks].sort((a, b) => {
    if (a.mark === null && b.mark === null) return 0;
    if (a.mark === null) return 1;
    if (b.mark === null) return -1;
    return lower ? a.mark - b.mark : b.mark - a.mark;
  });
  const out = new Map<string, number>();
  sorted.forEach((m, i) =>
    out.set(
      m.id,
      i > 0 && m.mark !== null && m.mark === sorted[i - 1]!.mark
        ? out.get(sorted[i - 1]!.id)!
        : i + 1,
    ),
  );
  return out;
}

/** League points: F1-style per placing among humans, plus a bonus for the best single event. */
export function leaguePoints(
  rows: Omit<MeetResultRow, 'leaguePoints' | 'bestEventBonus'>[],
): MeetResultRow[] {
  const humans = rows.filter((r) => !r.isBot);
  const bestHuman = Math.max(0, ...humans.map((r) => r.bestEvent));
  const bonusWinners = humans
    .filter((r) => r.bestEvent === bestHuman && bestHuman > 0 && humans.length > 1)
    .map((r) => r.id);
  return rows.map((r) => {
    if (r.isBot) return { ...r, leaguePoints: 0, bestEventBonus: false };
    const humanPlace = humans.filter((h) => h.place < r.place).length + 1;
    const bonus = bonusWinners.includes(r.id);
    return {
      ...r,
      leaguePoints: pointsForPlace(humanPlace) + (bonus ? BEST_EVENT_BONUS : 0),
      bestEventBonus: bonus,
    };
  });
}
